import { StarIcon } from '@heroicons/react/20/solid';
import { MapPinIcon, TrashIcon } from '@heroicons/react/24/outline';
import DOMPurify from 'dompurify';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { Loading, Tooltip } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import TikTokGlyph from 'src/assets/tiktok-glyph.svg';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { EventType, MediaType } from '../../../../../Chat.enum.ts';
import { parseSlides } from '../../../../../Chat.functions.ts';
import { tChatEvent } from '../../../../../Chat.types.ts';
import { isLottieSticker } from '../../../../../lottieSticker.functions.ts';
import useMessageBubbleMenu from '../../../../MessageContainer/components/EventBubble/components/MessageBubbleMenu/MessageBubbleMenu.hook.tsx';
import './FavoriteMessage.styles.scss';
import FavoriteMessageModal from './FavoriteMessageModal.component.tsx';

// Vite svgr exports default as React component at runtime, even though typed
// as string. Cast mirrors QuotedMessage.component.tsx.
const TikTokGlyphSvg = TikTokGlyph as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

export default function FavoriteMessage(props: tChatEvent) {
  const { t } = useTranslation('chat/chat');
  const { highlightMessages, loading } = useMessageBubbleMenu();
  const [openFavoriteMessageDetailModal, setOpenFavoriteMessageDetailModal] =
    useState(false);

  const [openLightbox, setOpenLightbox] = useState<boolean>(false);

  function onCloseLightbox() {
    setOpenLightbox(false);
  }

  function handleDeletehighlightedMessage() {
    highlightMessages(props.id, {
      type: EventType.MESSAGE,
      is_favorite: !props.message?.isFavorite,
    });
  }

  function renderMedia() {
    // Bound for the SHARE_POST case below. The switch keeps the long-form
    // discriminant so TypeScript's control-flow narrowing still reaches the
    // other cases, which read `props.content.attachments` directly.
    const attachment = props.content?.attachments?.[0];
    // INT-1708: an animated Lottie sticker is a zip, so the image branch below
    // would render it broken.
    if (isLottieSticker(attachment)) {
      return <p className="FavoriteMessage__Text">{t('stickerLabel')}</p>;
    }
    switch (props.content?.attachments?.[0]?.mediaType) {
      case MediaType.IMAGE:
      case MediaType.STICKER:
        if (isEmpty(props.content.attachments?.[0].url)) {
          return (
            <div onClick={() => setOpenLightbox(false)}>
              <img
                src="/images/storage/imagesIcon.png"
                alt={props.content.attachments?.[0].filename || ''}
                className="FavoriteMessage__Image"
              />
              <p className="FavoriteMessage__Text">
                {props.content?.attachments?.[0].filename}
              </p>
            </div>
          );
        }
        return (
          <img
            src={props.content.attachments?.[0].url || ''}
            alt={props.content.attachments?.[0].filename || ''}
            className="FavoriteMessage__Image"
            onClick={() => setOpenLightbox(true)}
          />
        );
      case MediaType.VIDEO:
        if (props?.content?.attachments?.[0].mimeType === 'video/quicktime') {
          return (
            <a
              href={props.content.attachments?.[0].url || ''}
              download={props.content.attachments?.[0].filename}
              target="_blank"
            >
              <img
                src="/images/storage/videoIcon.png"
                alt={props.content.attachments?.[0].filename || ''}
                className="FavoriteMessage__Video"
              />
              <p className="FavoriteMessage__Text">
                {props.content?.attachments?.[0].filename}
              </p>
            </a>
          );
        }
        return (
          <div onClick={() => setOpenLightbox(true)}>
            <img
              src="/images/storage/videoIcon.png"
              alt={props.content.attachments?.[0].filename || ''}
              className="FavoriteMessage__Video"
            />
            <p className="FavoriteMessage__Text">
              {props.content?.attachments?.[0].filename}
            </p>
          </div>
        );
      case MediaType.DOCUMENT:
        return (
          <a
            href={props.content.attachments?.[0].url || ''}
            download={props.content.attachments?.[0].filename}
            target="_blank"
          >
            <img
              src="/images/storage/docIcon.png"
              alt={props.content.attachments?.[0].filename || ''}
              className="FavoriteMessage__Doc"
            />
            <p className="FavoriteMessage__Text">
              {props.content?.attachments?.[0].filename}
            </p>
          </a>
        );
      case MediaType.AUDIO:
        return (
          <div onClick={() => setOpenLightbox(true)}>
            <img
              src="/images/storage/audioIcon.png"
              alt={props.content.attachments?.[0].filename || ''}
              className="FavoriteMessage__Audio"
            />
            <p className="FavoriteMessage__Text">
              {!isEmpty(props.content?.attachments?.[0].filename)
                ? props.content?.attachments?.[0].filename
                : 'Audio'}
            </p>
          </div>
        );
      case MediaType.LOCATION:
        return (
          <>
            <MapPinIcon className="FavoriteMessage__Location" />
            <p className="FavoriteMessage__Text">
              {!isEmpty(props.content?.attachments?.[0].filename)
                ? props.content?.attachments?.[0].filename
                : 'Ubicación'}
            </p>
          </>
        );
      // INT-929: TikTok shared video posts. Without this branch the switch
      // defaults to `null` and a favorited share renders as a blank cell.
      case MediaType.SHARE_POST:
        return (
          <>
            <TikTokGlyphSvg className="FavoriteMessage__SharePost" />
            <p
              className="FavoriteMessage__Text"
              data-testid="favorite-share-post"
            >
              {!isEmpty(attachment?.title)
                ? attachment?.title
                : t('sharedTikTokVideo')}
            </p>
          </>
        );
      default:
        return null;
    }
  }

  return (
    <>
      <div className="FavoriteMessage">
        <StarIcon className="FavoriteMessage__Content__Icon " />
        <div className="FavoriteMessage__Content">
          {props.content?.text ? (
            // Render via DOMPurify + innerHTML to mirror the chat bubble and the
            // expanded card (FavoriteMessageModal). The read-path escapes stray
            // <word> to &lt;word&gt; (messaging PR #903 / BUG-736); plain-text
            // React would show those entities literally in this preview (BUG-737).
            <p
              className="FavoriteMessage__Text"
              onClick={() => setOpenFavoriteMessageDetailModal(true)}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(props.content.text),
              }}
            />
          ) : null}
          {renderMedia()}
        </div>
        {loading ? (
          <Loading size="sm" color="primary" />
        ) : (
          <Tooltip message="Quitar destacado" position="left" color="neutral">
            <TrashIcon
              onClick={handleDeletehighlightedMessage}
              className="FavoriteMessage__Icon cursor-pointer"
            />
          </Tooltip>
        )}
      </div>
      <div>
        <span className="FavoriteMessage__Date">
          {moment(props.createdAt).format('DD/MM/YYYY')}
        </span>
      </div>
      {openLightbox && (
        <Lightbox
          plugins={[Zoom, Captions, Thumbnails, Download, Fullscreen, Video]}
          open={openLightbox}
          close={onCloseLightbox}
          slides={parseSlides(props.content?.attachments || [])}
          thumbnails={{ hidden: true }}
        />
      )}
      {!openLightbox && openFavoriteMessageDetailModal && (
        <FavoriteMessageModal
          open={openFavoriteMessageDetailModal}
          setOpen={setOpenFavoriteMessageDetailModal}
          messageData={props}
        />
      )}
    </>
  );
}
